import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import type { FeeRateInput, PaymentMethod } from "~/lib/queries";
import { parsePercentToBps, parseRupiah } from "./-fee-format";

/** A rate as typed: strings, so a half-typed "0." is not coerced away. */
export interface RateDraft {
  percent: string;
  fixed: string;
  costPercent: string;
  costFixed: string;
}

export const EMPTY_RATE: RateDraft = { percent: "", fixed: "", costPercent: "", costFixed: "" };

/**
 * Turns a draft into the API's shape, or says which field is wrong.
 *
 * Nothing is rounded: "0.705%" is refused rather than saved as 0.71%, because
 * a fee that quietly differs from the contract is worse than a form that
 * complains.
 */
export function toRateInput(
  method: PaymentMethod,
  draft: RateDraft,
): { rate: FeeRateInput } | { error: string } {
  const percentage_bps = parsePercentToBps(draft.percent);
  if (percentage_bps === null) return { error: `${method}: merchant % must be like 0.7 (at most two decimals).` };
  const cost_percentage_bps = parsePercentToBps(draft.costPercent);
  if (cost_percentage_bps === null) return { error: `${method}: DurianPay % must be like 0.7 (at most two decimals).` };
  const fixed = parseRupiah(draft.fixed);
  if (fixed === null) return { error: `${method}: merchant flat fee must be a rupiah amount like 4000.` };
  const cost_fixed = parseRupiah(draft.costFixed);
  if (cost_fixed === null) return { error: `${method}: DurianPay flat fee must be a rupiah amount like 4000.` };
  return { rate: { payment_method: method, percentage_bps, fixed, cost_percentage_bps, cost_fixed } };
}

/**
 * The four numbers a schedule is: what Kugie charges the merchant, and what
 * DurianPay charges Kugie, each as a percentage and a flat amount. QRIS is
 * usually only the percentage and VA only the flat fee; both are always
 * offered, because the API and the ledger support both.
 */
export function RateFields({
  idPrefix,
  draft,
  onChange,
}: {
  idPrefix: string;
  draft: RateDraft;
  onChange: (next: RateDraft) => void;
}) {
  const field = (key: keyof RateDraft, label: string, placeholder: string) => (
    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-${key}`} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input
        id={`${idPrefix}-${key}`}
        inputMode="decimal"
        value={draft[key]}
        placeholder={placeholder}
        onChange={(event) => onChange({ ...draft, [key]: event.target.value })}
        className="w-28"
      />
    </div>
  );

  return (
    <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
      <fieldset className="flex items-end gap-2">
        <legend className="mb-1.5 text-xs font-medium">Kugie charges the merchant</legend>
        {field("percent", "%", "0.7")}
        <span className="pb-2 text-muted-foreground">+</span>
        {field("fixed", "Flat Rp", "0")}
      </fieldset>
      <fieldset className="flex items-end gap-2">
        <legend className="mb-1.5 text-xs font-medium">DurianPay charges Kugie</legend>
        {field("costPercent", "%", "0.7")}
        <span className="pb-2 text-muted-foreground">+</span>
        {field("costFixed", "Flat Rp", "0")}
      </fieldset>
    </div>
  );
}
